import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { unionBy as _unionBy } from 'lodash';
import { GetAllRoutesMonitoring } from '@bringg/types';
import {
	convertFilterObjectValuesToRelevantValueTypes,
	FilterBar2,
	FilterBar2ListItem,
	getDefaultFilterBarTranslations
} from '@bringg/react-components';
import { useFilterQuery } from '@bringg-frontend/hooks';

import { useTeamsTreeData } from 'bringg-web/hooks';
import { useStores } from 'bringg-web/recipes';
import notification from 'bringg-web/services/notification';
import {
	runDateTranslations,
	runDriverNameTranslations,
	runIdTranslations,
	runOrderIdTranslations,
	RunsFilterItems,
	runStatusTranslations,
	runTeamNameTranslations,
	runTitleTranslations
} from 'bringg-web/features/runs/runs-filter/runs-filter.consts';
import { handleSearch, mapQueryToFetch, mapValuesFromFilterBar, mapValuesToFilterBar } from './routes-filter-bar-utils';
import { FilterBarListItemKeys, FilterQueryObject, InitialFilterQueryObject } from './types';
import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import useDateFormat from 'bringg-web/hooks/use-date-format';

import styles from './routes-filter.module.scss';

const RoutesFilter = () => {
	const { t } = useTranslation();
	const { teamsStore, plannedRoutesStore, runStore, tasksStore, driversStore } = useStores();
	const { routesMonitoringStore } = useRouteMonitoringStore();
	const [filterItems, setFilterItems] = useState<RunsFilterItems>({});

	const dateFormat = useDateFormat();

	const { initialFilterObjectFromQuery, setFilterObject: setFilterObjectForQuery } =
		useFilterQuery<FilterQueryObject>({
			resetUntrackedParamsOnChange: true
		});

	const teamOptions = useTeamsTreeData(teamsStore.all);

	const filterBarTranslations = useMemo(() => getDefaultFilterBarTranslations(t), [t]);

	const filterBarFilters = useMemo<FilterBar2ListItem[]>(
		() => [
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.IDS,
				title: runIdTranslations(t).runId,
				placeholder: runIdTranslations(t).runId,
				tooltip: runIdTranslations(t).runIdTooltip,
				multiselect: true,
				customProps: {
					onSearch: handleSearch
				},
				values: filterItems.ids || []
			},
			{
				type: FilterBar2.TYPES.DATE,
				name: FilterBarListItemKeys.DATE,
				title: runDateTranslations(t).date,
				tooltip: runDateTranslations(t).datesTooltip,
				customProps: {
					format: dateFormat,
					placeholder: runDateTranslations(t).date
				},
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.TITLE,
				title: runTitleTranslations(t).runTitle,
				placeholder: runTitleTranslations(t).runTitle,
				tooltip: runTitleTranslations(t).runTitleTooltip,
				multiselect: true,
				customProps: {
					onSearch: handleSearch
				},
				values: filterItems.by_title_ids || []
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.STATUS,
				title: runStatusTranslations(t).runStatus,
				placeholder: runStatusTranslations(t).runStatus,
				tooltip: runStatusTranslations(t).runStatusTooltip,
				values: [
					{ label: runStatusTranslations(t).runStatusClosed, value: 'close' },
					{ label: runStatusTranslations(t).runStatusOpen, value: 'open' }
				],
				applyFilterOnValueChange: true
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.TASKS,
				title: runOrderIdTranslations(t).orderId,
				placeholder: runOrderIdTranslations(t).orderId,
				tooltip: runOrderIdTranslations(t).orderIdTooltip,
				multiselect: true,
				customProps: {
					onSearch: handleSearch
				},
				values: filterItems.task_ids || []
			},
			{
				type: FilterBar2.TYPES.TREE_SELECT,
				name: FilterBarListItemKeys.TEAMS,
				placeholder: runTeamNameTranslations(t).teamName,

				// This is just teams...
				title: filterBarTranslations.subTeams,
				values: teamOptions
			},
			{
				type: FilterBar2.TYPES.SELECT,
				name: FilterBarListItemKeys.DRIVERS,
				title: runDriverNameTranslations(t).driverName,
				placeholder: runDriverNameTranslations(t).driverName,
				tooltip: runDriverNameTranslations(t).driverNameTooltip,
				multiselect: true,
				customProps: {
					onSearch: handleSearch
				},
				values: filterItems.driver_ids || []
			}
		],
		[filterItems, teamOptions.length]
	);

	const filterObjectUpdated = async (filterObject: FilterQueryObject) => {
		setFilterObjectForQuery(filterObject);
		let query;

		try {
			query = await mapQueryToFetch(filterObject);
			if (query.ids?.length === 0) {
				routesMonitoringStore.reset();
				return;
			}
			routesMonitoringStore.setFilterQuery(query);

			await routesMonitoringStore.fetchAfterFilterUpdated();
		} catch (err) {
			console.error('failed fetching data', err);
			notification.error(t('ROUTE_MONITORING.FILTERS.ERROR_FETCHING_DATA'));
		}
	};

	const initialValues = useMemo<InitialFilterQueryObject>(() => {
		const values = convertFilterObjectValuesToRelevantValueTypes(
			filterBarFilters,
			initialFilterObjectFromQuery
		) as FilterQueryObject;
		filterObjectUpdated(values).catch(err => {
			console.error('failed to load routes ', err);
		});

		return mapValuesToFilterBar(values);
	}, [initialFilterObjectFromQuery]);

	useEffect(() => {
		const initFilters = async () => {
			try {
				const { ids, by_title_ids, task_ids, driver_ids } = initialValues;

				const [runsIds, runsByTitle, tasksByIds, driversByIds] = await Promise.all([
					runStore.loadAllByIds((ids as number[]) || []),
					plannedRoutesStore.batchGet((by_title_ids as number[]) || []),
					tasksStore.loadMany((task_ids as number[]) || []),
					driversStore.loadMany(driver_ids || []),
					teamsStore.fetchAll()
				]);

				setFilterItems({
					ids: _unionBy(routesMonitoringStore.runs, runsIds as GetAllRoutesMonitoring, 'id').map(
						({ external_id, id }) => ({
							label: external_id,
							value: id
						})
					),
					by_title_ids: runsByTitle.map(route => ({
						label: route?.title,
						value: route?.id
					})),
					task_ids: tasksByIds.map(({ title, id }) => ({
						label: title,
						value: id
					})),
					driver_ids: driversByIds.map(driver => ({
						label: driver?.name,
						value: driver?.id
					}))
				});
			} catch (error) {
				console.error('Routes filter bar - failed while fetching data: ', error);
				notification.error(t('ROUTE_MONITORING.FILTERS.ERROR_FETCHING_DATA'));
			}
		};

		if (!Object.keys(filterItems).length) {
			initFilters();
		}
	}, [initialValues]);

	return (
		<FilterBar2
			className={styles.routesFilter}
			list={filterBarFilters}
			onFilter={async filterObject => {
				await filterObjectUpdated(mapValuesFromFilterBar(filterObject));
			}}
			initialValues={initialValues}
			translations={filterBarTranslations}
			applyFilterImmediately
		/>
	);
};

export default observer(RoutesFilter);
